//src/utils/opaque-login.utils.ts
import { KE2, type OpaqueClient } from '@cloudflare/opaque-ts';

import { OPAQUE_CONFIG, SERVER_IDENTITY } from '@/lib/opaque-config';

import { bytesToHex, hexToBytes, unwrapResult, createOpaqueClient, EMPTY_LENGTH } from './opaque-utils';

/**
 * Result of OPAQUE authentication initialization
 */
export interface OpaqueAuthInit {
  ke1: string;
  client: OpaqueClient;
}

/**
 * Result of OPAQUE authentication finalization
 */
export interface OpaqueAuthFinish {
  ke3: string;
  clientSessionKey: string;
}

/**
 * Creates an OPAQUE authentication initialization (KE1)
 *
 * This is Step 1 of the OPAQUE login flow:
 * 1. Client generates KE1 from password
 * 2. KE1 is sent to server for authentication
 *
 * @param password - User's password
 * @returns KE1 as hex string and the client instance (must be reused for authFinish)
 * @throws Error if KE1 creation fails
 */
export async function createAuthInit(password: string): Promise<OpaqueAuthInit> {
  const client = createOpaqueClient();

  // Generate KE1
  const ke1Result = await client.authInit(password);
  const ke1 = unwrapResult(ke1Result, 'Failed to create KE1');

  return {
    ke1: bytesToHex(ke1.serialize()),
    client, // Return client for reuse in finalizeAuth
  };
}

/**
 * Finalizes OPAQUE authentication by processing server's KE2
 *
 * This is Step 2 of the OPAQUE login flow:
 * 1. Receives server's KE2 response
 * 2. Processes KE2 and generates KE3
 * 3. Derives session key
 *
 * @param client - The OPAQUE client instance from createAuthInit (must be reused)
 * @param ke2Hex - Server's KE2 response (hex string)
 * @param username - User's username (used for verification)
 * @returns KE3 and client session key for verification
 * @throws Error if authentication fails or credentials are invalid
 */
export async function finalizeAuth(
  client: OpaqueClient,
  ke2Hex: string,
  username: string
): Promise<OpaqueAuthFinish> {
  // Deserialize server's KE2
  const ke2Bytes = hexToBytes(ke2Hex);
  const ke2Msg = KE2.deserialize(OPAQUE_CONFIG, ke2Bytes);

  // Process KE2 and generate KE3
  try {
    const finishRes = await client.authFinish(ke2Msg, SERVER_IDENTITY, username);
    const { ke3, session_key: clientSessionKeyBytes } = unwrapResult(
      finishRes,
      'Invalid username or password'
    );

    return {
      ke3: bytesToHex(ke3.serialize()),
      clientSessionKey: bytesToHex(clientSessionKeyBytes),
    };
  } catch (error) {
    throw new Error('Invalid username or password');
  }
}

/**
 * Complete OPAQUE authentication flow helper
 *
 * Combines both steps of OPAQUE authentication:
 * 1. Creates KE1 from password
 * 2. Processes server's KE2 and verifies session key
 *
 * Note: This function is NOT used directly because the client instance
 * must persist between createAuthInit and finalizeAuth calls.
 * Use createAuthInit and finalizeAuth separately instead.
 *
 * @param client - The OPAQUE client instance from createAuthInit
 * @param username - User's username
 * @param serverResponse - Server's response containing sessionId and KE2
 * @returns Object with sessionId, KE3, and client session key for final verification
 * @throws Error if authentication fails or session key doesn't match
 */
export async function performOpaqueAuth(
  client: OpaqueClient,
  username: string,
  serverResponse: { sessionId: string; ke2: string }
): Promise<{ sessionId: string; ke3: string; clientSessionKey: string }> {
  const { sessionId, ke2 } = serverResponse;

  // Validate server response
  if (sessionId.length === EMPTY_LENGTH || ke2.length === EMPTY_LENGTH) {
    throw new Error('Invalid server response: missing sessionId or ke2');
  }

  // Finalize authentication with the same client instance
  const { ke3, clientSessionKey } = await finalizeAuth(client, ke2, username);

  return {
    sessionId,
    ke3,
    clientSessionKey,
  };
}
